import { Button, Card, DisabledReason, Select, STATUS_TONES } from '@hgc/ui';
import { canSwitchMode, modeTable, type VehicleKind } from '@hgc/ardupilot';
import { vehicleKindOfState, type CommandRequest } from '@hgc/core';
import { useState, type JSX } from 'react';

import { useVehicleState } from '../runtime/hooks';
import { requestOf, useCommandDispatch, type CommandRunner, type ConfirmPrompt } from './commanding';

/**
 * 飞行工具条：解锁/上锁、起飞、降落、返航、模式切换。
 *
 * 三条规则的落地方式：
 *
 * 1. **禁用必须说明原因。** 每个按钮都包在 `DisabledReason` 里，原因来自
 *    `CommandManager.check()` 的 `blockers`（"未解锁，无法起飞""链路不可用…"）。
 *    只把按钮变灰，等于让用户去猜。
 * 2. **危险动作必须二次确认。** 凡是会立刻改变飞行状态的命令都先过
 *    `useConfirm({ anchor: event.currentTarget })`，确认后才带上 `confirmed: true` 下发。
 * 3. **失败要给出具体原因。** 命令的失败文案来自管理器，它已经把飞控在 ACK 前后
 *    用 `STATUSTEXT` 播报的那句话拼了进去。
 */

export interface FlightToolbarProps {
  readonly runner: CommandRunner;
}

/** 起飞高度的默认值。给一个具体的数而不是留空：留空的起飞命令在 ArduPilot 上会原地不动。 */
const DEFAULT_TAKEOFF_ALT_M = 10;

interface ActionSpec {
  readonly id: string;
  readonly label: string;
  readonly variant: 'primary' | 'secondary' | 'danger';
  readonly request: CommandRequest;
  readonly prompt: ConfirmPrompt;
}

export function FlightToolbar({ runner }: FlightToolbarProps): JSX.Element {
  const state = useVehicleState();
  const dispatch = useCommandDispatch(runner);

  const armed = state?.armed ?? false;
  const kind = state === null ? 'unknown' : vehicleKindOfState(state);

  const armRequest = requestOf('MAV_CMD_COMPONENT_ARM_DISARM', [armed ? 0 : 1, 0]);
  const takeoffRequest = requestOf('MAV_CMD_NAV_TAKEOFF', [
    0,
    0,
    0,
    0,
    Number.NaN,
    Number.NaN,
    DEFAULT_TAKEOFF_ALT_M,
  ]);
  const landRequest = requestOf('MAV_CMD_NAV_LAND', [0, 0, 0, 0, Number.NaN, Number.NaN, 0]);
  const rtlRequest = requestOf('MAV_CMD_NAV_RETURN_TO_LAUNCH');

  const actions: readonly ActionSpec[] = [
    {
      id: 'arm',
      label: armed ? '上锁（停桨）' : '解锁电机',
      variant: armed ? 'danger' : 'primary',
      request: armRequest,
      prompt: {
        title: armed ? '立即上锁（停桨）？' : '解锁电机？',
        consequence: armed
          ? '上锁会立刻停止螺旋桨。若机体在空中，上锁等于坠机，且无法撤销。'
          : '解锁后螺旋桨会随油门指令转动，无法撤销；请先确认机体周围没有人。',
        confirmLabel: armed ? '立即上锁' : '立即解锁',
        danger: 'warning',
      },
    },
    {
      id: 'takeoff',
      label: '起飞',
      variant: 'secondary',
      request: takeoffRequest,
      prompt: {
        title: '立即起飞？',
        consequence: `机体将立刻从当前位置自动爬升到 ${DEFAULT_TAKEOFF_ALT_M} 米并进入引导模式，下发之后无法撤回。`,
        confirmLabel: '立即起飞',
        danger: 'warning',
      },
    },
    {
      id: 'land',
      label: '降落',
      variant: 'secondary',
      request: landRequest,
      prompt: {
        title: '立即降落？',
        consequence: '机体将立刻在当前位置开始降落，落地后螺旋桨停止转动。这个过程无法撤销。',
        confirmLabel: '立即降落',
        danger: 'warning',
      },
    },
    {
      id: 'rtl',
      label: '返航',
      variant: 'secondary',
      request: rtlRequest,
      prompt: {
        title: '执行返航？',
        consequence: '机体将立刻返回返航点并按飞控配置的高度与方式降落，无法撤销。',
        confirmLabel: '立即返航',
        danger: 'warning',
      },
    },
  ];

  return (
    <Card
      title="飞行操作"
      subtitle="每条指令都经过命令管理器：ACK 与重传由它处理，失败时给出飞控播报的具体原因"
      level={armed ? 'caution' : 'neutral'}
      actions={
        <div className="flex items-center gap-1">
          {runner.busy ? (
            <span
              data-hgc-command-busy=""
              className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_TONES.info.text} ${STATUS_TONES.info.surface} ${STATUS_TONES.info.border}`}
            >
              等待飞控确认…
            </span>
          ) : null}
          <span
            data-hgc-armed-state={armed ? 'armed' : 'disarmed'}
            className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${
              armed
                ? `${STATUS_TONES.caution.text} ${STATUS_TONES.caution.surface} ${STATUS_TONES.caution.border}`
                : `${STATUS_TONES.neutral.text} ${STATUS_TONES.neutral.surface} ${STATUS_TONES.neutral.border}`
            }`}
          >
            {state === null ? '没有机体数据' : armed ? '已解锁 · 螺旋桨会转' : '未解锁 · 电机安全'}
          </span>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) => (
          <DisabledReason key={action.id} reason={runner.disabledReason(action.request)}>
            <Button
              variant={action.variant}
              size="md"
              onClick={(event) => {
                void dispatch(action.request, action.prompt, event.currentTarget);
              }}
            >
              {action.label}
            </Button>
          </DisabledReason>
        ))}
      </div>

      <ModeRow runner={runner} kind={kind} />
      <Feedback runner={runner} />
    </Card>
  );
}

/**
 * 模式切换：下拉框只负责"选目标"，真正下发的是旁边的按钮。
 *
 * 分开的理由是模式切换属于"会立刻改变飞行状态"的动作：把选择和下发揉在一个
 * `onChange` 里，意味着用户在下拉框里滚过一项目标模式就会触发一次命令。
 */
function ModeRow({
  runner,
  kind,
}: {
  readonly runner: CommandRunner;
  readonly kind: VehicleKind;
}): JSX.Element {
  const state = useVehicleState();
  const dispatch = useCommandDispatch(runner);
  const [targetMode, setTargetMode] = useState<number | null>(null);

  const currentMode = state?.customMode ?? null;
  const selected = targetMode ?? currentMode;
  const options = modeTable(kind).entries.map((entry) => ({
    value: String(entry.number),
    label: entry.label ?? entry.name,
  }));

  const request: CommandRequest | null =
    selected === null ? null : requestOf('MAV_CMD_DO_SET_MODE', [1, selected, 0]);

  // 领域层的换模式门禁：它只编码有据可依的拒绝（需要位置估计、遥控失控、模式号不存在）
  const verdict =
    selected === null
      ? null
      : canSwitchMode(kind, selected, {
          currentModeNumber: currentMode ?? undefined,
          armed: state?.armed ?? false,
          hasPositionEstimate: state?.latDeg != null,
          // 失控状态在遥测里没有直接的布尔量（只能从遥控通道推断），所以这里不猜
          rcFailsafe: false,
        });

  const blockedReason = verdict !== null && !verdict.allowed ? verdict.message : undefined;
  const disabledReason =
    request === null
      ? '还没有收到当前模式：等一帧 HEARTBEAT 到达后再切换'
      : (blockedReason ?? runner.disabledReason(request));

  const target = selected === null ? null : modeTable(kind).entries.find((entry) => entry.number === selected);
  const name = target?.label ?? target?.name ?? (selected === null ? '未知模式' : `#${String(selected)}`);

  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-line pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-muted">飞行模式</span>
        <Select
          aria-label="目标飞行模式"
          size="sm"
          value={selected === null ? '' : String(selected)}
          placeholder="（还没收到模式）"
          options={options}
          onChange={(value) => setTargetMode(value === '' ? null : Number(value))}
        />
        <DisabledReason reason={disabledReason}>
          <Button
            variant="secondary"
            size="sm"
            onClick={(event) => {
              if (request === null) return;
              void dispatch(
                request,
                {
                  title: `切换到「${name}」？`,
                  consequence:
                    `将立刻把飞控切到「${name}」模式。若该模式需要操作员介入（引导、自动一类的模式），` +
                    '切换后机体可能马上开始动作，这个过程无法撤销。',
                  confirmLabel: `切换到「${name}」`,
                  danger: 'warning',
                },
                event.currentTarget,
              );
            }}
          >
            切换模式
          </Button>
        </DisabledReason>
      </div>

      {state === null ? null : (
        <p className="m-0 text-[10px] text-fg-subtle">
          当前模式：{state.modeName ?? '--'}
          {verdict?.source === undefined ? '' : ` · 换模式门禁依据：${verdict.source}`}
        </p>
      )}

      {blockedReason === undefined ? null : (
        <p
          data-hgc-mode-blocked=""
          className={`m-0 rounded-control border-l-4 px-2 py-1 text-xs font-semibold ${STATUS_TONES.warning.borderLeft} ${STATUS_TONES.warning.surface} ${STATUS_TONES.warning.text}`}
        >
          {blockedReason}
        </p>
      )}
    </div>
  );
}

/**
 * 最近一条命令的结论。
 *
 * 失败时这里显示的必须是**具体原因**，所以它直接把管理器的 `text` 贴出来 ——
 * 绝不在这一层改写成人人都能猜到的"失败"。成功也给一条，因为"什么都没发生"
 * 与"命令被接受了"在界面上必须能区分。
 */
function Feedback({ runner }: { readonly runner: CommandRunner }): JSX.Element | null {
  const feedback = runner.feedback;
  if (feedback === null) return null;
  const tone = STATUS_TONES[feedback.level];

  return (
    <div
      data-hgc-command-feedback={feedback.level}
      className={`mt-3 flex items-start gap-2 rounded-control border-l-4 px-2 py-1.5 ${tone.borderLeft} ${tone.surface}`}
    >
      <div className="min-w-0 flex-1">
        <p className={`m-0 text-xs font-semibold ${tone.text}`}>{feedback.title}</p>
        <p data-hgc-command-feedback-text="" className="m-0 mt-0.5 text-xs break-words text-fg">
          {feedback.text}
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={runner.dismissFeedback}>
        关闭
      </Button>
    </div>
  );
}
